(function(root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.BadgeBuzzPricingMatrix = api;
})(typeof window !== 'undefined' ? window : globalThis, function() {
  'use strict';

  var MAX_CANDIDATES = 256;

  function text(value) {
    return value == null ? '' : String(value).trim();
  }

  function normalized(value) {
    return text(value).toLowerCase();
  }

  function valueFor(configuration, label) {
    var direct = configuration && configuration[label];
    if (direct != null) return text(direct);
    var normalizedLabel = normalized(label);
    var match = Object.keys(configuration || {}).find(function(key) {
      return normalized(key) === normalizedLabel;
    });
    return match ? text(configuration[match]) : '';
  }

  function pricingGroups(definition) {
    var labels = Array.isArray(definition && definition.pricingGroups)
      ? definition.pricingGroups
      : [];
    return labels.map(function(label) {
      return (definition.groups || []).find(function(group) {
        return normalized(group.label) === normalized(label);
      });
    }).filter(Boolean);
  }

  function canonicalConfiguration(definition, configuration) {
    var result = {};
    if (definition && definition.material) {
      result.Material = text(definition.material);
    }
    if (definition && definition.forcedDesign) {
      result.Design = text(definition.forcedDesign);
    }
    Object.keys((definition && definition.pricingDefaults) || {}).forEach(function(label) {
      var configured = valueFor(configuration || {}, label);
      var fallback = text(definition.pricingDefaults[label]);
      if (configured || fallback) result[label] = configured || fallback;
    });
    pricingGroups(definition).forEach(function(group) {
      var value = valueFor(configuration || {}, group.label);
      if (value) result[group.label] = value;
    });
    Object.keys((definition && definition.pricingFixedValues) || {}).forEach(function(label) {
      var value = text(definition.pricingFixedValues[label]);
      if (value) result[label] = value;
    });
    if (definition && (definition.lanyardFamily || definition.standaloneCustomization)) {
      var customization = definition.standaloneCustomization || result.Customization || definition.defaultCustomization;
      if (customization === 'Solid Color') {
        result.Customization = customization;
        delete result['Print method'];
      } else {
        delete result.Color;
        if (definition.lanyardFamily) result.Customization = 'Customized';
        else delete result.Customization;
      }
    }
    return result;
  }

  function configurationKey(definition, configuration) {
    var canonical = canonicalConfiguration(definition, configuration);
    return Object.keys(canonical).sort().map(function(key) {
      return encodeURIComponent(normalized(key)) + '=' +
        encodeURIComponent(normalized(canonical[key]));
    }).join('&');
  }

  function variantId(variant) {
    var raw = text(variant && variant.id);
    var digits = raw.match(/\d+/g);
    return digits ? digits.join('') : '';
  }

  function variantValue(variant, index) {
    if (Array.isArray(variant && variant.options) && variant.options[index] != null) {
      return text(variant.options[index]);
    }
    return text(variant && variant['option' + (index + 1)]);
  }

  function matchingVariant(context, configuration) {
    var optionNames = Array.isArray(context && context.options)
      ? context.options.map(function(option) {
          return text(typeof option === 'string' ? option : option && option.name);
        }).filter(Boolean)
      : [];
    var variants = Array.isArray(context && context.variants)
      ? context.variants
      : [];
    return variants.find(function(variant) {
      return optionNames.every(function(name, index) {
        var selected = valueFor(configuration, name);
        return !selected || normalized(variantValue(variant, index)) === normalized(selected);
      });
    }) || (optionNames.length ? null : variants[0]) || null;
  }

  function combinations(groups, index, current, output) {
    if (output.length >= MAX_CANDIDATES) return;
    if (index >= groups.length) {
      output.push(Object.assign({}, current));
      return;
    }
    var group = groups[index];
    (group.options || []).forEach(function(option) {
      if (output.length >= MAX_CANDIDATES) return;
      if (Array.isArray(option.supportedWidths) && option.supportedWidths.length) {
        var selectedWidth = valueFor(current, 'Width');
        if (
          selectedWidth &&
          !option.supportedWidths.some(function(width) {
            return normalized(width) === normalized(selectedWidth);
          })
        ) return;
      }
      current[group.label] = text(option && option.value);
      combinations(groups, index + 1, current, output);
    });
    delete current[group.label];
  }

  function buildCandidates(context, definition) {
    if (!definition || definition.matrix === false) return [];
    var groups = pricingGroups(definition);
    if (!groups.length) return [];
    var rawCombinations = [];
    if (definition.lanyardFamily || definition.standaloneCustomization) {
      // Color is irrelevant to printing; stock has exactly one width/hardware.
      // Do not cross-product these branches: it would truncate valid offers.
      if (definition.standaloneCustomization !== 'Solid Color') {
        combinations(groups.filter(function(group) {
          return group.label !== 'Color' && group.label !== 'Customization';
        }), 0, {Customization: 'Customized'}, rawCombinations);
      }
      if (definition.solidColorPricing || definition.standaloneCustomization === 'Solid Color') {
        var colors = groups.find(function(group) { return group.label === 'Color'; });
        ((colors && colors.options) || []).forEach(function(color) {
          if (color.value === 'Custom') return;
          ['Single Clip', 'Double Clip'].forEach(function(attachment) {
            rawCombinations.push({Customization:'Solid Color', Color:color.value, Width:'3/8"',
              'Attachment / Clips':attachment, 'Attachment type':'Iron Polished silver bulldog clip'});
          });
        });
      }
    } else {
      combinations(groups, 0, {}, rawCombinations);
    }
    var seen = new Set();
    var candidates = [];
    rawCombinations.some(function(configuration) {
      var canonical = canonicalConfiguration(definition, configuration);
      var key = configurationKey(definition, canonical);
      var variant = matchingVariant(context || {}, canonical);
      var id = variantId(variant);
      if (!key || !id || seen.has(key)) return false;
      seen.add(key);
      candidates.push({
        key: key,
        variant_gid: 'gid://shopify/ProductVariant/' + id,
        configuration: canonical,
      });
      return candidates.length >= MAX_CANDIDATES;
    });
    return candidates;
  }

  return {
    MAX_CANDIDATES: MAX_CANDIDATES,
    canonicalConfiguration: canonicalConfiguration,
    configurationKey: configurationKey,
    buildCandidates: buildCandidates,
  };
});
